import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";

export default function LoginPage({ lang }) {
  const copy = dictionary[lang];
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: copy.seo.loginTitle,
      description: copy.seo.loginDescription,
    });
  }, [copy.seo.loginDescription, copy.seo.loginTitle, lang]);

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const response = await api.post("/auth/login", form);
      login(response.data);
      toast.success(copy.common.saveSuccess);

      const fallbackPath = localizePath(lang, "/dashboard");
      navigate(location.state?.from || fallbackPath, { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      let errorMessage = copy.common.requestFailed;
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        errorMessage = detail[0].msg;
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-auth" data-testid="login-page-root">
      <div className="flex w-full max-w-lg flex-col gap-4">
        <Link to={localizePath(lang)} data-testid="login-page-home-link">
          <Button
            variant="outline"
            className="rounded-full border-gold-500/30 bg-white/80 text-stone-800 hover:bg-white"
            data-testid="login-page-home-button"
          >
            {copy.nav.home}
          </Button>
        </Link>

        <Card className="w-full border-gold-500/20 shadow-xl" data-testid="login-page-card">
          <CardHeader data-testid="login-page-card-header">
            <CardTitle className="text-3xl" data-testid="login-page-title">{copy.auth.loginTitle}</CardTitle>
            <p className="text-sm text-stone-600" data-testid="login-page-subtitle">{copy.auth.loginSubtitle}</p>
          </CardHeader>

          <CardContent data-testid="login-page-card-content">
            <form className="space-y-4" onSubmit={onSubmit} data-testid="login-page-form">
              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="login-page-email-label">{copy.auth.email}</label>
                <Input
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  type="email"
                  required
                  data-testid="login-page-email-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-stone-700" data-testid="login-page-password-label">{copy.auth.password}</label>
                <Input
                  value={form.password}
                  onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
                  type="password"
                  required
                  minLength={8}
                  data-testid="login-page-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-stone-900 py-6 text-cream-50 hover:bg-stone-700"
                data-testid="login-page-submit-button"
              >
                {submitting ? copy.common.loading : copy.auth.submitLogin}
              </Button>
            </form>

            <Link className="mt-5 block text-sm text-gold-700 underline" to={localizePath(lang, "/staff/register")} data-testid="login-page-register-link">
              {copy.auth.goToRegister}
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
